"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.scrubRoute = scrubRoute;
exports.scrubEvent = scrubEvent;
exports.scrubBreadcrumb = scrubBreadcrumb;
// Scrubbing shared by homey-app and the backend. Both send events to Sentry, a third party, so
// secrets and PII are masked here before anything leaves. Design and known gaps:
// .claude/plans/sentry.md.
const emailPattern = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;
// US numbers as people write them. Longer digit runs (ms timestamps, most ids) survive; a bare
// 10-digit run such as a seconds timestamp is masked too, which errs on the safe side.
const phonePattern = /(?<!\d)(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g;
const jwtPattern = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const bearerPattern = /Bearer\s+\S+/gi;
// Web URLs and the app's own deep-link schemes (OAuth callbacks carry ?code=). The match never
// ends on prose punctuation, so "…?id=1: 500" keeps its colon.
const urlPattern = /\b(?:https?|capacitor|io\.myhomey\.app(?:\.test)?):\/\/[^\s"'<>]*[^\s"'<>:,;.)]/g;
// Any parameter left over in text: relative URLs ("/api/x?resetToken=…") and router messages
// ("URL Segment: 'callback?code=…'"). Keeps the key for debugging, drops the value, but not the
// prose punctuation after it ("/a?id=1: 500" keeps its colon).
const paramPattern = /([?&#][\w.~%-]+=)(?:[^&#\s"'<>]*[^&#\s"'<>:,;.)])?/g;
// Angular matrix params ("/reset;token=…"), which stripQuery leaves in place. Same punctuation rule.
const matrixParamPattern = /(;[\w.~%-]+=)(?:[^;/?#&()\s"'<>]*[^;/?#&()\s"'<>:,.])?/g;
// Secrets in JSON logged as text ('{"resetToken":"…"}', also escaped inside another string).
// Other JSON values are kept: emails and phone numbers in them are masked below.
const jsonSecretPattern = /(\\?"(?:[\w-]*(?:token|password|secret|otp|credential|session|api_?key)[\w-]*|authorization)\\?"\s*:\s*)(?:\\?"(?:[^"\\]|\\[^"])*\\?"|[^\s,}\]]+)/gi;
function scrubText(text) {
    return text
        // e.g. "Http failure response for https://…?id=…" logged to the console before a capture.
        .replace(urlPattern, stripQuery)
        .replace(paramPattern, '$1[redacted]')
        .replace(matrixParamPattern, '$1[redacted]')
        .replace(jsonSecretPattern, '$1"[redacted]"')
        .replace(jwtPattern, '[token]')
        .replace(bearerPattern, 'Bearer [token]')
        .replace(emailPattern, '[email]')
        .replace(phonePattern, '[phone]');
}
/** Query strings and fragments carry OAuth codes, tokens and emails; the path is enough. */
function stripQuery(url) {
    return url.split(/[?#]/)[0];
}
/**
 * A URL with its query and fragment gone and the rest scrubbed like any text: paths can carry
 * matrix params or an email. Like any text, a 10-digit path segment is masked as a phone number.
 */
function scrubUrl(url) {
    return scrubText(stripQuery(url));
}
/** The URL's path, with segments holding a digit as `:id`: one issue per endpoint, not per apartment. */
function routeOf(url) {
    let path;
    try {
        path = new URL(url).pathname;
    }
    catch {
        path = stripQuery(url);
    }
    return path.split('/').map(segment => /\d/.test(segment) ? ':id' : segment).join('/');
}
/** A request path as a tag, which scrubEvent does not touch: one value per endpoint, scrubbed. */
function scrubRoute(url) {
    return scrubText(routeOf(url));
}
function scrubEvent(event) {
    // Sentry serializes non-Error values and console arguments whole into extra (URLs, bodies, tokens).
    delete event.extra;
    if (event.request) {
        const userAgent = event.request.headers?.['User-Agent'];
        event.request = {
            url: event.request.url === undefined ? undefined : scrubUrl(event.request.url),
            // Sentry derives browser and OS from it; Referer and the rest can carry query strings.
            headers: userAgent === undefined ? undefined : { 'User-Agent': userAgent },
        };
    }
    if (event.user) {
        // Belt and braces: setSentryUser only sets the id, but nothing else may add more.
        event.user = { id: event.user.id };
    }
    if (event.message) {
        event.message = scrubText(event.message);
    }
    for (const exception of event.exception?.values ?? []) {
        if (exception.value) {
            exception.value = scrubText(exception.value);
        }
        // Server SDKs can attach source lines and local variables to frames; neither is sent.
        for (const frame of exception.stacktrace?.frames ?? []) {
            delete frame.pre_context;
            delete frame.context_line;
            delete frame.post_context;
            delete frame.vars;
        }
    }
    // JS breadcrumbs were scrubbed as they were recorded, but on iOS/Android the native SDK merges
    // in its own, which never pass through beforeBreadcrumb.
    event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb);
    return event;
}
const urlKeys = new Set(['url', 'from', 'to']);
// Console breadcrumbs keep the raw arguments; the scrubbed message is enough. Native network
// breadcrumbs carry the query and fragment in keys of their own.
const droppedKeys = new Set(['arguments', 'http.query', 'http.fragment']);
// Sentry normalizes breadcrumb data to a few levels anyway; this only bounds the recursion.
const maxDataDepth = 5;
function scrubBreadcrumb(breadcrumb) {
    const scrubbed = {
        ...breadcrumb,
        message: breadcrumb.message === undefined ? undefined : scrubText(breadcrumb.message),
    };
    if (breadcrumb.data === undefined) {
        return scrubbed;
    }
    return { ...scrubbed, data: scrubData(breadcrumb.data, 0) };
}
/** Scrubs every string in breadcrumb data, however deeply nested (native breadcrumbs nest). */
function scrubData(data, depth) {
    return Object.fromEntries(Object.entries(data)
        .filter(([key]) => !droppedKeys.has(key))
        .map(([key, value]) => [key, typeof value === 'string' && urlKeys.has(key) ? scrubUrl(value) : scrubValue(value, depth + 1)]));
}
function scrubValue(value, depth) {
    if (typeof value === 'string') {
        return scrubText(value);
    }
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (depth > maxDataDepth) {
        return '[truncated]';
    }
    return Array.isArray(value) ? value.map(item => scrubValue(item, depth + 1)) : scrubData(value, depth);
}
